import { getBytes, ref } from "firebase/storage";
import { storage } from "../../../firebase";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { isLphBookingNode } from "./BookingReportChecklistUtils";
import { getCHGMedicalCertTemplate, getCHGStudyDates, appendCHGStudyHours } from "../Export/ExportTab/medicalCertificateTemplateUtils";
import { LPH_BOOKING_CERT_TEMPLATES, normalizeCertificateDoctor } from "../Export/ExportTab/lphMedicalCertificateTemplates";

export const BOOKING_CERT_TEMPLATES = LPH_BOOKING_CERT_TEMPLATES;
export const getBookingMedicalCertTemplate = (node, doctor) => {
  if (!isLphBookingNode(node)) throw new Error("Medical certificates are generated for LPH only.");
  const file = BOOKING_CERT_TEMPLATES[normalizeCertificateDoctor(doctor)];
  if (!file) throw new Error("No PSG medical certificate template is configured for this doctor.");
  return file;
};
export const getBookingDownloadCertTemplate = (node, doctor, order = "") => {
  if (isLphBookingNode(node)) return `templates/medical_cert_embedded/${getBookingMedicalCertTemplate(node, doctor)}`;
  if (/^(CHG(?:03|09|11|304)?)$/i.test(String(node || "").trim()) || /chularat/i.test(String(node || ""))) {
    return `templates/medical_cert/${getCHGMedicalCertTemplate(order || node)}`;
  }
  throw new Error("No node-specific medical certificate template is configured for this node.");
};
export const getBookingCertificateAge = (dateOfBirth, today = new Date()) => {
  const dateText = String(dateOfBirth || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateText)) return "";
  const birth = new Date(`${dateText}T00:00:00`);
  const [year, month, day] = dateText.split("-").map(Number);
  if (Number.isNaN(birth.getTime()) || birth > today || birth.getFullYear() !== year || birth.getMonth() + 1 !== month || birth.getDate() !== day) return "";
  const beforeBirthday = today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
  return String(today.getFullYear() - birth.getFullYear() - (beforeBirthday ? 1 : 0));
};
export const getBookingMedicalCertData = (values) => {
  const required = ["patientName", "studyDate", "ahi", "minSpo2"];
  if (required.some((key) => values[key] === "" || values[key] === undefined || values[key] === null)) {
    throw new Error("Confirm the patient name, study date, AHI and minimum SpO2 from the report.");
  }
  if (!Number.isFinite(Number(values.ahi)) || Number(values.ahi) < 0) throw new Error("AHI must be a non-negative number.");
  if (required.includes("minSpo2") && (!Number.isFinite(Number(values.minSpo2)) || Number(values.minSpo2) <= 0 || Number(values.minSpo2) > 100)) throw new Error("Minimum SpO2 must be between 0 and 100.");
  const date = new Date(`${values.studyDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) throw new Error("Select a valid study date.");
  const thaiDate = (day) => `วันที่ ${day.toLocaleDateString("th-TH", { day: "numeric", month: "long", year: "numeric" })}`;
  return {
    DOCTOR_SCORED_DATE_TH: thaiDate(new Date()), PATIENT_NAME: values.patientName,
    STUDY_DATE_TH: thaiDate(date), AHI: String(values.ahi), MIN_SPO2: String(values.minSpo2 || ""),
    GENDER: values.gender || "", AGE: values.age || "", HEIGHT: values.height || "",
    HEIGHT_CM: values.height || "", WEIGHT: values.weight || "", WEIGHT_KG: values.weight || "",
    BMI: values.bmi || "", LOCATION: values.node, STUDY_DATE: values.studyDate,
    SLEEP_DATE: values.studyDate, HN: values.hn, "SS-XX": values.order, SS_XX: values.order,
    INTER_PHY: values.doctor,
  };
};
const renderBookingCertificate = async (templatePath, data, chgStudyDate) => {
  const content = await getBytes(ref(storage, templatePath));
  const document = new Docxtemplater(new PizZip(content), {
    paragraphLoop: true, linebreaks: true, delimiters: { start: "<", end: ">" },
    parser: (tag) => ({ get: (scope) => scope[tag.trim()] }),
    nullGetter: () => "",
  });
  document.render(data);
  const output = document.getZip();
  if (chgStudyDate) appendCHGStudyHours(output, chgStudyDate);
  // The source templates highlight editable fields; finished certificates do not.
  output.file(/^word\/.*\.xml$/).forEach((entry) => {
    const xml = entry.asText();
    const cleaned = xml.replace(/<w:highlight\b(?=[^>]*\bw:val=["']yellow["'])[^>]*\/\s*>/gi, "");
    if (cleaned !== xml) output.file(entry.name, cleaned);
  });
  return output.generate({ type: "blob", compression: "DEFLATE", mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
};
export const generateBookingMedicalCertBlob = async (values) => {
  const file = getBookingMedicalCertTemplate(values.node, values.doctor);
  return renderBookingCertificate(`templates/medical_cert_embedded/${file}`, getBookingMedicalCertData(values));
};
export const generateBookingDownloadCertBlob = async (values) => {
  const templatePath = getBookingDownloadCertTemplate(values.node, values.doctor, values.order);
  if (isLphBookingNode(values.node)) return generateBookingMedicalCertBlob(values);
  const studyDate = new Date(`${values.studyDate}T12:00:00`);
  const age = getBookingCertificateAge(values.dateOfBirth);
  if (!String(values.patientName || "").trim() || Number.isNaN(studyDate.getTime()) || age === "" || !values.gender) {
    throw new Error("Confirm the patient name, study date, date of birth and gender before downloading the certificate.");
  }
  const thaiDate = (date) => `วันที่ ${date.toLocaleDateString("th-TH", { day: "numeric", month: "long", year: "numeric" })}`;
  const [year, month, day] = values.dateOfBirth.slice(0, 10).split("-");
  return renderBookingCertificate(templatePath, {
    PATIENT_NAME: values.patientName, HN_REF: values.hospitalNumber || values.hn,
    DOB: `${day}/${month}/${year}`, AGE: age, GENDER: values.gender,
    CURRENT_DATE_TH: getCHGStudyDates(values.studyDate).studyDateThai, STUDY_DATE_TH: thaiDate(studyDate),
  }, values.studyDate);
};
